import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, change } from 'redux-form';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { getStorageSetting, getCicAlert, saveCicAlertReviewState } from 'actions';
import WorkflowStages from 'common/Workflows/WorkflowStages';
import CicClosureReason from 'common/CiCAlerts/ClosureReason';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import LoadingWrapper from 'components/Common/LoadingWrapper';
import ModalWrapper from 'components/Modals/ModalWrapper';
import RadioTableGroup from 'components/Common/Inputs/RadioTableGroup';
import FormViewButton from 'components/Common/Buttons/FormViewButton';

import ReviewerComments from './ReviewerComment';

class CicAlertModal extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    isShown: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    isCicAlertLoading: PropTypes.bool,
    id: PropTypes.number,
    cicAlert: PropTypes.shape({
      id: PropTypes.number,
      isCic: PropTypes.bool,
      hasCuringDocs: PropTypes.bool,
      changes: PropTypes.arrayOf(PropTypes.shape()),
      affectedForms: PropTypes.arrayOf(PropTypes.shape()),
      workflow: PropTypes.shape(),
      referenceCode: PropTypes.string,
      customerDbId: PropTypes.number,
      review: PropTypes.shape(),
      customerId: PropTypes.string,
      customerName: PropTypes.string,
      daysBeforeExpiration: PropTypes.string,
      dataPoint: PropTypes.string,
      presumedCic: PropTypes.bool,
      closureReason: PropTypes.number,
      curingDocs: PropTypes.arrayOf(PropTypes.shape()),
    })
  };

  static defaultProps = {
    id: null,
    cicAlert: null,
    permissions: null,
    isCicAlertLoading: false,
  };

  constructor(props) {
    super();
    this.state = {
      isCicUpdated: false,
      currentCuringDocs: null
    };
  }

  componentDidMount() {
    const { dispatch, id } = this.props;
    dispatch(getCicAlert(id));
  }

  componentDidUpdate(prevProps, prevState) {
    const { cicAlert } = this.props;

    if (prevProps.cicAlert == null && cicAlert != null && cicAlert.changes != null && cicAlert.changes.length > 0) {
      cicAlert.changes.map((chg, index) => (
        this.updateFormValue(index, chg.isCic, chg.hasCuringDocs)
      ));
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (state.currentChanges == null
      && props.cicAlert != null
      && props.cicAlert.changes != null) {
      return {
        currentChanges: props.cicAlert.changes,
        currentCuringDocs: props.cicAlert.curingDocs,
        initialChangesString: JSON.stringify(props.cicAlert.changes)
      };
    }
    return {};
  }

  onCicChange(e, newVal, prevVal, index) {
    const { currentChanges, isCicUpdated } = this.state;
    currentChanges[index].isCic = newVal ? (newVal === 'true') : null;
    this.setState({
      currentChanges,
      isCicUpdated: !isCicUpdated,
    });
  }

  onHasCuringDocChange(e, newVal, prevVal, index) {
    const { currentChanges } = this.state;
    currentChanges[index].hasCuringDocs = newVal ? (newVal === 'true') : null;
    this.setState({
      currentChanges
    });
  }

  onClearSubmissionError() {
    this.setState({ submissionError: null });
  }

  onCuringDocAdded(file, response) {
    let { currentCuringDocs } = this.state;

    if (currentCuringDocs == null) {
      currentCuringDocs = [];
    }

    if (response && response.length > 0) {
      currentCuringDocs.push({
        id: response[0].id,
        originalFileName: file.name
      });

      this.setState({ currentCuringDocs });
    }
  }

  onCuringDocRemoved(file) {
    let { currentCuringDocs } = this.state;

    if (currentCuringDocs == null) {
      currentCuringDocs = [];
    }

    if (file) {
      currentCuringDocs = currentCuringDocs.filter(f => f.id !== file.responseId);
      this.setState({ currentCuringDocs });
    }
  }

  onFormSubmit(id, workflowAction) {
    const { dispatch } = this.props;
    const { currentChanges, currentCuringDocs } = this.state;
    let submissionError = null;
    if (workflowAction != null) {
      if (workflowAction.actionId === '1') {
        // Submit button pressed
        currentChanges.forEach((element) => {
          if (element.isCic === null) {
            submissionError = { code: 500, message: 'Please answer \'Yes\' or \'No\' to all the questions above before submitting this decision.' };
          } else if (element.isCic === true && element.hasCuringDocs === null) {
            submissionError = { code: 500, message: 'You must indicate if you have located and referenced relevant curing documentation for all possible changes in circumstance.' };
          } else if (element.isCic === true && element.hasCuringDocs === true && (!currentCuringDocs || currentCuringDocs.length === 0 || !workflowAction.draftComments)) {
            submissionError = { code: 500, message: 'Curing documentation must be uploaded as evidence along with an explanatory comment.' };
          } else if (element.isCic === false && !workflowAction.draftComments) {
            submissionError = { code: 500, message: 'Commentary is required to explain why the alert is not a change in circumstance.' };
          }
        });
      }

      this.setState({ submissionError }, () => {
        if (submissionError != null) {
          if (this.submissionErrors) {
            this.submissionErrors.scrollIntoView({ behavior: 'smooth' });
          }
          return;
        }

        const reviewState = Object.assign({}, workflowAction);
        reviewState.changes = currentChanges;
        reviewState.curingDocs = currentCuringDocs ? currentCuringDocs.map(f => f.id) : null;

        dispatch(saveCicAlertReviewState(id, reviewState));
      });
    }
  }

  alertIsExpired = () => {
    const { cicAlert } = this.props;
    return cicAlert && cicAlert.closureReason && cicAlert.closureReason === CicClosureReason.Expired;
  };

  getModalTitle = () => {
    const { cicAlert } = this.props;
    return <React.Fragment>{ `Alert ${ cicAlert != null
      ? cicAlert.referenceCode : '' }` }<span className="red-text">{ this.alertIsExpired()
        ? ' - Closed as relevant forms have expired' : '' }</span></React.Fragment>;
  };

  updateFormValue(index, isCic, hasCuringDocs) {
    const { dispatch } = this.props;
    dispatch(change('cureCicAlertForm', `changes.[${ index }].isCic`, isCic));
    dispatch(change('cureCicAlertForm', `changes.[${ index }].hasCuringDocs`, hasCuringDocs));
  }

  isValidReviewer() {
    const { permissions, cicAlert } = this.props;

    if (cicAlert == null || cicAlert.workflow == null || this.alertIsExpired()) {
      return false;
    }

    // WF Stage 1 or 2 - Draft or Pending Signature
    if (cicAlert.workflow.stage === WorkflowStages.Draft || cicAlert.workflow.stage === WorkflowStages.PendingSignature) {
      return true;
    }

    const isOwner = cicAlert.workflow != null && cicAlert.workflow.assignedUser === getStorageSetting('username');
    // WF Stage 3 or 4 and Initial Reviewer
    // WF Stage 5 or 6 and Secondary Reviewer
    // WF Stage 7, 8 or 100 and Expert Reviewer
    // WF Stage 100 and Supplementary Reviewer
    const initialReviewer = (cicAlert.workflow.stage === WorkflowStages.PendingReview || cicAlert.workflow.stage === WorkflowStages.UnderInitialReview)
      && AccessControl.hasPermission(permissions, [ActivityPerms.monitoringResolver]);
    const secondaryReviewer = (cicAlert.workflow.stage === WorkflowStages.PendingSecondaryReview || cicAlert.workflow.stage === WorkflowStages.UnderSecondaryReview)
      && AccessControl.hasPermission(permissions, [ActivityPerms.monitoringResolver]);
    const expertReviewer = (cicAlert.workflow.stage === WorkflowStages.PendingExpertReview || cicAlert.workflow.stage === WorkflowStages.UnderExpertReview || cicAlert.workflow.stage === WorkflowStages.Complete)
      && AccessControl.hasPermission(permissions, [ActivityPerms.monitoringExpertReviewer]);
    const suppReviewer = cicAlert.workflow.stage === 100 && AccessControl.hasPermission(permissions, [ActivityPerms.monitoringResolver]);

    if (cicAlert.workflow.stage === WorkflowStages.Complete) {
      return (expertReviewer || suppReviewer);
    }

    return (isOwner && (initialReviewer || secondaryReviewer || expertReviewer || suppReviewer));
  }

  disableFormUpdate() {
    const { permissions, cicAlert } = this.props;

    if (cicAlert == null || cicAlert.workflow == null) {
      return true;
    }

    const isOwner = cicAlert.workflow != null && cicAlert.workflow.assignedUser === getStorageSetting('username');
    const hasReviewerPermission = AccessControl.hasPermission(permissions, [ActivityPerms.monitoringResolver, ActivityPerms.monitoringExpertReviewer]);

    // WF Stage 1 or 2 - Draft or Pending Signature
    if (cicAlert.workflow.stage === WorkflowStages.Draft || cicAlert.workflow.stage === WorkflowStages.PendingSignature) {
      return false;
    }

    // WF Stage 100 - Complete
    if (cicAlert.workflow.stage === WorkflowStages.Complete) {
      return true;
    }

    return !(isOwner && hasReviewerPermission);
  }

  render() {
    const {
      cicAlert,
      isShown,
      onCancel,
      permissions,
      isCicAlertLoading,
      id
    } = this.props;
    const {
      submissionError,
      currentChanges
    } = this.state;

    const disableFormUpdate = this.disableFormUpdate();
    return (
      <ModalWrapper
        headerClose
        isOpen={ isShown }
        title={ this.getModalTitle() }
        btnCancelTitle="Close"
        onCancel={ onCancel }
        showFooter={ false }
        modalSize="modal-lg cic-modal"
      >
        { isCicAlertLoading && <LoadingWrapper loaded={ !isCicAlertLoading } /> }
        { !isCicAlertLoading && cicAlert && <div>
          <div className="panel-body">
            <div className="cic-alert-section">
              <div className="columns">
                <div className="col-6">
                  <div className="columns">
                    <div className="col-4 panel-item-name">
                      <p>Customer</p>
                    </div>
                    <div className="col-8 panel-item-value">
                      { AccessControl.hasPermission(permissions, [ActivityPerms.customerPageView, ActivityPerms.customerEdit]) ? <p>
                        <Link className="text-bold" target="_blank" to={ `/customer/${ cicAlert.customerDbId }` }>{ cicAlert.customerId } - { cicAlert.customerName }</Link></p>
                        : <p>{ cicAlert.customerId } - { cicAlert.customerName }</p>}
                    </div>
                  </div>
                </div>
              </div>
              <p className="info">Review the following possible Change(s) in Circumstance (CiC):</p>
              { currentChanges != null && currentChanges.map((chg, index) => chg != null && <div key={ chg.changeType }>
                <div className="columns data-row">
                  <div className="col-12">
                    <div className="columns">
                      <div className="col-2 panel-item-name">
                        <p>Date Alert Raised</p>
                      </div>
                      <div className="col-10 panel-item-value">
                        <p>{ chg.dateCicDetermined != null ? moment(chg.dateCicDetermined).format('MMM DD, YYYY HH:mm:ss') : 'N/A' }</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="columns data-row">
                  <div className="col-6">
                    <div className="columns">
                      <div className="col-4 panel-item-name">
                        <p>Description</p>
                      </div>
                      <div className="col-8 panel-item-value">
                        <p>{ chg.changeTypeLongDescription != null ? chg.changeTypeLongDescription : 'N/A' }</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="columns">
                      <div className="col-4 panel-item-name">
                        <p>Data Point</p>
                      </div>
                      <div className="col-8 panel-item-value">
                        <p>{ chg.changeDataPoints != null ? chg.changeDataPoints : 'N/A' }</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="columns data-row">
                  <div className="col-6">
                    <div className="columns">
                      <div className="col-4 panel-item-name">
                        <p>New Value</p>
                      </div>
                      <div className="col-8 panel-item-value">
                        <p>{ chg.current != null && chg.current !== '' ? chg.current : 'N/A' }</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="columns">
                      <div className="col-4 panel-item-name">
                        <p>Old Value</p>
                      </div>
                      <div className="col-8 panel-item-value">
                        <p>{ chg.previous != null && chg.previous !== '' ? chg.previous : 'N/A' }</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="columns">
                  <div className="col-6">
                    <div className="columns">
                      <div className="col-4 panel-item-name">
                        <p>CiC grace period applicable?</p>
                      </div>
                      <div className="col-8 panel-item-value">
                        <span>{ chg.isCic ? 'Yes' : 'No' }</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="columns">
                      <div className="col-4 panel-item-name">
                        <p>Presumed a CiC?</p>
                      </div>
                      <div className="col-8 panel-item-value">
                        <span>{ chg.presumedCic ? 'Yes' : 'No' }</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="columns">
                  <div className="col-6">
                    <div className="columns">
                      <div className="col-4 panel-item-name">
                        <label htmlFor="cic-choice" className="form-label panel-item-name">Is this a CiC?</label>
                      </div>
                      <div className="col-8 panel-item-value">
                        { !disableFormUpdate && <RadioTableGroup
                          name={ `changes[${ index }].isCic` }
                          items={ [[{ text: 'Yes', value: 'true' }, { text: 'No', value: 'false' }]] }
                          preventReset
                          normalize={ value => value }
                          onChange={ (e, newVal, prevVal) => this.onCicChange(e, newVal, prevVal, index) }
                          disabled={ disableFormUpdate }
                        /> }
                        {
                          // eslint-disable-next-line no-nested-ternary
                          disableFormUpdate && <label className="form-label text-bold">{ chg.isCic != null ? chg.isCic ? 'Yes' : 'No' : 'Unknown' }</label>
                        }
                      </div>
                    </div>
                  </div>
                  { chg.isCic && <div className="col-6">
                    <div className="columns">
                      <div className="col-4 panel-item-name">
                        <label htmlFor="cic-doc-ans" className="form-label panel-item-name">Have you located and referenced relevant curing documentation?</label>
                      </div>
                      <div className="col-8 panel-item-value">
                        { !disableFormUpdate && <RadioTableGroup
                          name={ `changes[${ index }].hasCuringDocs` }
                          items={ [[{ text: 'Yes', value: 'true', className: 'align-top' }, { text: 'No - a new form is required', value: 'false' }]] }
                          preventReset
                          normalize={ value => value }
                          onChange={ (e, newVal, prevVal) => this.onHasCuringDocChange(e, newVal, prevVal, index) }
                          disabled={ disableFormUpdate }
                        /> }
                        {
                          // eslint-disable-next-line no-nested-ternary
                          disableFormUpdate && <label className="form-label text-bold">{ chg.hasCuringDocs != null ? chg.hasCuringDocs ? 'Yes' : 'No' : 'Unknown' }</label>
                        }
                      </div>
                    </div>
                  </div>}
                </div>
                <hr className="cic-divide" />
              </div>)}
              { cicAlert.affectedForms != null && <div>
                <p className="info">If this Alert is a CiC, these form(s) will expire:</p>
                <table className="table table-striped table-hover table-bordered">
                  <thead>
                    <tr>
                      <th style={ { textAlign: 'center' } }>Form</th>
                      <th style={ { textAlign: 'center' } }>Account</th>
                      <th style={ { textAlign: 'center' } }>Days until Expiry</th>
                      <th style={ { textAlign: 'center' } }>Form Replaced?</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    { cicAlert.affectedForms.length > 0 && cicAlert.affectedForms.map(form => (
                      <tr key={ `${ form.formId }${ form.accountId }` }>
                        <td align="center">{ form.formTypeDescription }</td>
                        <td align="center">{ form.accountName }</td>
                        <td align="center">{ form.daysBeforeExpiration }</td>
                        <td align="center">{ form.formReplaced ? 'Yes' : 'No' }</td>
                        <td>
                          <FormViewButton
                            content={ <span className="icon icon-apps" /> }
                            className="btn btn-sm"
                            title="Reopen"
                            formId={ form.formId }
                            accountId={ form.accountId }
                            form={ form.workflowState && {
                              accountId: form.accountId,
                              formType: form.formTypeText,
                              workflowStageDescription: form.workflowState.stageDescription,
                              workflowStatus: form.workflowState.stageText,
                              workflowUsername: form.workflowState.assignedUser,
                              workflowUserFullName: form.workflowState.assignedUserFullName
                            } }
                            permissions={ permissions }
                          />
                        </td>
                      </tr>
                    ))}
                    { cicAlert.affectedForms.length <= 0 && <tr><td colSpan="7" style={ { textAlign: 'center' } }><p><b>No Data to Show</b></p></td></tr> }
                  </tbody>
                </table>
                <p className="mt-1rem">If this Alert is not a CiC, the above will not be forced to expire.</p>
                <hr className="cic-divide" />
              </div>}
            </div>
            { cicAlert && <ReviewerComments
              id={ !Number.isNaN(id) ? parseInt(id, 10) : null }
              customerId={ !Number.isNaN(cicAlert.customerDbId) ? parseInt(cicAlert.customerDbId, 10) : null }
              cicReviewState={ cicAlert.review }
              validReviewer={ this.isValidReviewer() }
              allowFormUpdate={ this.alertIsExpired() }
              permissions={ permissions }
              onSubmit={ (wfId, values) => this.onFormSubmit(wfId, values) }
              onReviewCommentsChanged={ () => this.setState({ submissionError: null }) }
              onSupportingDocAdded={ (f, r) => this.onCuringDocAdded(f, r) }
              onSupportingDocRemoved={ (f, r) => this.onCuringDocRemoved(f, r) }
            /> }
            { submissionError && submissionError.code !== 0 && <div
              className="toast toast-error"
              ref={ (el) => { this.submissionErrors = el; } }
            >
              <button type="button" onClick={ () => this.onClearSubmissionError() } className="btn btn-clear float-right" />
              { submissionError.message }
            </div>}
          </div>
        </div> }
        { !isCicAlertLoading && !cicAlert && <div>
          <p className="info">There is no alert data to display.</p>
        </div>}
      </ModalWrapper>
    );
  }
}

const CureCicAlertForm = reduxForm({
  form: 'cureCicAlertForm',
  enableReinitialize: true
})(CicAlertModal);

const mapStateToProps = (state) => {
  const {
    auth: {
      permissions
    },
    monitoring: {
      cicAlert,
      isCicAlertLoading,
    }
  } = state;
  return {
    permissions,
    cicAlert,
    isCicAlertLoading
  };
};

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CureCicAlertForm);
